import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "../auth/AuthContext";
import useAutoRetry from "../hooks/useAutoRetry";
import { gradeCodeOf } from "./adapter";
import { useClassrooms } from "./ClassroomContext";
import repository from "./repository";
import { cacheLessons, getCachedLessons } from "./contentCache";
import { DENIED, RETRYABLE, problemOf } from "./problems";
import { readJson, writeJson } from "./storage";

const LessonsContext = createContext(null);

const initialState = (userId) => {
  const cached = getCachedLessons(userId);
  return cached
    ? { status: "ready", lessons: cached, fromCache: true, problem: null }
    : { status: "loading", lessons: [], fromCache: false, problem: null };
};

// Lessons are organised class → week → subject → lesson. A class is shown
// the lessons of its own grade; each class remembers its own week, because
// two classes are not always on the same week.
export const lessonsForClass = (lessons, classroom) => {
  const grade = gradeCodeOf(classroom?.grade);
  return grade ? lessons.filter((lesson) => lesson.gradeCode === grade) : lessons;
};

// [{ key, name, lessons }] for one week, in a stable order by subject name.
export const subjectsOf = (lessons) =>
  [
    ...lessons
      .reduce((all, lesson) => {
        const subject = all.get(lesson.subjectKey) || {
          key: lesson.subjectKey,
          name: lesson.subject || "Other lessons",
          lessons: [],
        };
        subject.lessons.push(lesson);
        return all.set(lesson.subjectKey, subject);
      }, new Map())
      .values(),
  ].sort((a, b) => a.name.localeCompare(b.name));

export const LessonsProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user.id;
  const { active } = useClassrooms();
  const weekKey = `week:${userId}:${active?.id}`;

  const [state, setState] = useState(() => initialState(userId));
  // Before weeks were per class there was one per teacher; it is a fair
  // starting point for each class until the teacher chooses.
  const [chosenWeek, setChosenWeek] = useState(() =>
    readJson(weekKey, readJson(`week:${userId}`, null))
  );

  // Quiet reloads (automatic retries) keep the current screen instead of
  // flashing the loading state.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setState((s) =>
        s.status === "error" ? { ...s, status: "loading", problem: null } : s
      );
    }
    try {
      const lessons = await repository.listLessons();
      cacheLessons(userId, lessons);
      setState({ status: "ready", lessons, fromCache: false, problem: null });
    } catch (error) {
      const problem = problemOf(error);
      // Lessons saved on this device stay usable unless access was refused.
      setState((s) =>
        s.fromCache && problem !== DENIED
          ? { ...s, problem }
          : { status: "error", lessons: [], fromCache: false, problem }
      );
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep checking while there are no lessons yet or the last load failed.
  const autoRetrying =
    RETRYABLE.has(state.problem) ||
    (state.status === "ready" && state.lessons.length === 0);
  const retryQuietly = useCallback(() => load({ quiet: true }), [load]);
  useAutoRetry(autoRetrying, retryQuietly);

  const chooseWeek = useCallback(
    (week) => {
      writeJson(weekKey, week);
      setChosenWeek(week);
    },
    [weekKey]
  );

  const value = useMemo(() => {
    const classLessons = lessonsForClass(state.lessons, active);
    const weeks = [
      ...new Set(classLessons.map((l) => l.week).filter(Boolean)),
    ].sort((a, b) => a - b);
    // The current week is not in the data contract, so infer it only when
    // there is exactly one week; otherwise use the teacher's remembered choice.
    const week = weeks.includes(chosenWeek)
      ? chosenWeek
      : weeks.length === 1
      ? weeks[0]
      : null;
    const lessonsThisWeek = classLessons.filter((l) => l.week === week);
    const subjects = subjectsOf(lessonsThisWeek);
    return {
      ...state,
      lessons: classLessons,
      retry: () => load(),
      autoRetrying,
      weeks,
      week,
      chooseWeek,
      lessonsThisWeek,
      subjects,
      findSubject: (key) => subjects.find((subject) => subject.key === key) || null,
      findLesson: (slug) => classLessons.find((l) => l.slug === slug) || null,
    };
  }, [state, active, chosenWeek, load, chooseWeek, autoRetrying]);

  return (
    <LessonsContext.Provider value={value}>{children}</LessonsContext.Provider>
  );
};

export const useLessons = () => useContext(LessonsContext);
